import { ReactComponent as CI_DEVTECH_WHITE } from '@/assets/ci/devtech-white.svg';
import BG_GNB from '@/assets/images/common/bg-gnb.png';
import { ReactComponent as HamburgerIcon } from '@/assets/images/icons/icn-hamburger.svg';
import { ReactComponent as OutlinkIcon } from '@/assets/images/icons/icn-outlink.svg';

import Divider from '@/components/atoms/Divider';
import ExternalLink from '@/components/atoms/ExternalLink';
import { LINK_CAREERS, LINK_DEVTECH_YOUTUBE, LINK_GITHUB } from '@/constants/links';
import { Alternate, HTML_LANG, listPath, useLang, useT } from '@/i18n';
import { DeleteIcon, GlobalIcon } from '@devsisters/wds-icons';
import { cn, Dialog, MergeProps } from '@devsisters/wds-react';
import { Link } from 'gatsby';
import React from 'react';
import { useMediaQuery } from 'usehooks-ts';

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const FADEIN_DURATION = 200;

type DialogProps = Dialog.DialogProps;
type BaseProps = {
  className?: string;
  languageSwitch: Alternate;
};

type Props = MergeProps<DialogProps, BaseProps>;
function GNBDialog(props: Props) {
  const { className, languageSwitch, open, onOpenChange, ...dialogProps } = props;
  const lang = useLang();
  const t = useT();

  // 닫기 애니메이션
  const [_open, setOpen] = React.useState(open);
  const [isClosing, toggleClosing] = React.useState(false);
  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (open) {
        setOpen(open);
        return;
      }

      toggleClosing(true);

      return sleep(200).then(() => {
        setOpen(open);
        toggleClosing(false);
        onOpenChange?.(open);
      });
    },
    [onOpenChange],
  );

  const isLargeScreen = useMediaQuery('(min-width: 1200px)');
  React.useLayoutEffect(() => {
    if (isLargeScreen) {
      handleOpenChange?.(false);
    }
  }, [isLargeScreen]);

  return (
    <Dialog.Root open={open ?? _open} onOpenChange={handleOpenChange} {...dialogProps}>
      <Dialog.Trigger className={cn('flex h-13 w-13 items-center justify-center', className)}>
        <HamburgerIcon width={20} />
      </Dialog.Trigger>

      <Dialog.Overlay
        className={cn(
          'z-50 animate-fade-in p-0',
          isClosing && 'opacity-0 transition-opacity duration-[--animate-duration]',
        )}
        style={{ '--animate-duration': `${FADEIN_DURATION}ms` }}
      >
        <Dialog.Content
          className={'relative h-full w-full space-y-0 rounded-none p-0'}
          maxWidth={'none'}
          style={{ backgroundImage: `url(${BG_GNB})`, backgroundSize: 'cover' }}
        >
          <Dialog.Close>
            <button className={'absolute right-3 top-0 flex h-13 w-13 text-white flex-center'} type={'button'}>
              <DeleteIcon width={20} />
            </button>
          </Dialog.Close>
          <Dialog.Title className={'!mx-7 !mt-7'}>
            <span hidden>{'메뉴'}</span>
            <Link to={listPath(lang)} onClick={() => handleOpenChange(false)}>
              <CI_DEVTECH_WHITE height={16} />
            </Link>
          </Dialog.Title>
          <Dialog.Description></Dialog.Description>
          <div
            className={cn('flex flex-col justify-center gap-y-9', 'mx-12 h-full text-title-20 font-bold text-white')}
          >
            <Dialog.Close asChild>
              <Link to={listPath(lang)}>{t('nav.blog')}</Link>
            </Dialog.Close>
            <Divider className={'bg-white/30'} />
            <Dialog.Close asChild>
              <ExternalLink href={LINK_GITHUB}>
                <span>{'GitHub'}</span>
                <OutlinkIcon width={16} />
              </ExternalLink>
            </Dialog.Close>
            <Divider className={'bg-white/30'} />
            <Dialog.Close asChild>
              <ExternalLink href={LINK_DEVTECH_YOUTUBE}>
                <span>{'YouTube'}</span>
                <OutlinkIcon width={16} />
              </ExternalLink>
            </Dialog.Close>
            <Divider className={'bg-white/30'} />
            <Dialog.Close asChild>
              <ExternalLink href={LINK_CAREERS}>
                <span>{t('nav.careers')}</span>
                <OutlinkIcon width={16} />
              </ExternalLink>
            </Dialog.Close>
            <Divider className={'bg-white/30'} />
            <Dialog.Close asChild>
              <Link
                className={'flex items-center gap-3'}
                to={languageSwitch.path}
                hrefLang={HTML_LANG[languageSwitch.lang]}
              >
                <GlobalIcon width={20} />
                <span>{t('nav.switchLang')}</span>
              </Link>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog.Root>
  );
}

export default GNBDialog;
